import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Modal from "@mui/material/Modal";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SuiviTimeline from "../eleves/SuiviTimeline";
import EmailModal from "../../components/EmailModal";
import api from "../../services/api";
import { formatPlanning } from "utils/planning";
import { useTranslation } from "react-i18next";
import { formatDate } from "utils/formatDate";
import { libelleMatiere } from "utils/valeurs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

const pulseKeyframes = {
  "@keyframes coursPulse": {
    "0%, 100%": { opacity: 1, transform: "scale(1)" },
    "50%": { opacity: 0.45, transform: "scale(0.85)" },
  },
};

const cardStyle = {
  background: "#fff",
  borderRadius: "16px",
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  height: "100%",
};

const labelStyle = {
  color: "#9e9e9e", fontSize: 12, fontWeight: 500,
  textTransform: "uppercase", letterSpacing: "0.06em",
};

const TYPE_THEMES = {
  individuel: {
    label: "valeurs.typeCours.individuel", icon: "person", color: "#0d47a1",
    gradient: "linear-gradient(135deg, #1565c0, #0d47a1)",
  },
  groupe: {
    label: "valeurs.typeCours.groupe", icon: "groups", color: "#4a148c",
    gradient: "linear-gradient(135deg, #6a1b9a, #4a148c)",
  },
  general: {
    label: "valeurs.typeCours.general", icon: "campaign", color: "#1b5e20",
    gradient: "linear-gradient(135deg, #388e3c, #2e7d32)",
  },
};

const MATIERES = {
  quoran: { bg: "#e8f5e9", color: "#2e7d32" },
  arabe: { bg: "#e3f2fd", color: "#1565c0" },
  science: { bg: "#fff3e0", color: "#e65100" },
};

const fieldStyle = {
  width: "100%", height: "40px", padding: "0 12px",
  borderRadius: "8px", border: "1px solid #d2d6da",
  fontSize: "14px", color: "#344767", outline: "none",
  background: "#fff", boxSizing: "border-box", fontFamily: "inherit",
};

const textareaStyle = {
  ...fieldStyle, height: "auto", padding: "10px 12px", resize: "vertical",
};

function Section({ title, icon, color = "#2e7d32", children }) {
  return (
    <MDBox mb={3}>
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <MDBox sx={{
          width: 28, height: 28, borderRadius: "8px",
          background: `linear-gradient(135deg, ${color}, ${color}cc)`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon sx={{ color: "#fff", fontSize: 14 }}>{icon}</Icon>
        </MDBox>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767", fontSize: 15 }}>
          {title}
        </MDTypography>
      </MDBox>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={1}>
        {children}
      </Grid>
    </MDBox>
  );
}

function AvatarCircle({ prenom, nom, photo, color }) {
  return (
    <MDBox sx={{
      width: 48, height: 48, borderRadius: "50%",
      background: color, color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: 16, flexShrink: 0,
      overflow: "hidden", border: `2px solid ${color}44`,
    }}>
      {photo ? (
        <img src={`${photo}`} alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        `${prenom?.[0] || ""}${nom?.[0] || ""}`
      )}
    </MDBox>
  );
}

// ── MODAL AJOUT ABSENCE ──
function AbsenceModal({ open, onClose, cours, onSaved }) {
  const { t } = useTranslation();
  const isGroupe = Boolean(cours?.groupe_id);
  const eleves = cours?.groupe?.eleves || [];

  const [form, setForm] = useState({
    eleve_id: "",
    date: new Date().toISOString().slice(0, 10),
    motif: "",
    commentaire: "",
    rattrapage_prevu: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        eleve_id: !isGroupe ? cours?.eleve_id || "" : "",
        date: new Date().toISOString().slice(0, 10),
        motif: "",
        commentaire: "",
        rattrapage_prevu: "",
      });
      setError("");
    }
  }, [open, cours, isGroupe]);

  const handleSubmit = async () => {
    if (!form.date) { setError(t("cours.absence.erreurDate")); return; }
    if (isGroupe && !form.eleve_id) { setError(t("cours.absence.erreurEleve")); return; }
    setSaving(true);
    try {
      await api.post("/absences", {
        cours_id: cours.id,
        eleve_id: isGroupe ? form.eleve_id : cours.eleve_id,
        date: form.date,
        motif: form.motif || null,
        commentaire: form.commentaire || null,
        rattrapage_prevu: form.rattrapage_prevu || null,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || t("commun.erreurEnregistrement"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <MDBox sx={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "95%", sm: 480 },
        background: "#fff", borderRadius: "20px",
        boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
        outline: "none", overflow: "hidden",
      }}>
        {/* Header */}
        <MDBox px={3} py={2.5} sx={{
          background: "linear-gradient(135deg, #c62828, #e53935)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <MDBox>
            <MDTypography variant="h6" color="white" fontWeight="bold">
              {t("cours.absence.titre")}
            </MDTypography>
            <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
              {isGroupe ? cours?.groupe?.nom : `${cours?.eleve?.prenom} ${cours?.eleve?.nom}`}
            </MDTypography>
          </MDBox>
          <MDBox onClick={onClose} sx={{
            width: 32, height: 32, borderRadius: "50%",
            background: "rgba(255,255,255,0.2)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            "&:hover": { background: "rgba(255,255,255,0.35)" },
          }}>
            <Icon sx={{ color: "#fff", fontSize: 18 }}>close</Icon>
          </MDBox>
        </MDBox>

        <MDBox p={3}>
          {error && (
            <MDBox mb={2} p={1.5} sx={{ background: "#ffebee", borderRadius: "8px" }}>
              <MDTypography variant="caption" color="error">{error}</MDTypography>
            </MDBox>
          )}

          {/* Élève (si groupe) */}
          {isGroupe && (
            <MDBox mb={2}>
              <MDTypography variant="caption" fontWeight="bold" sx={{
                display: "block", mb: 0.5, textTransform: "uppercase", fontSize: 11, color: "#9e9e9e",
              }}>
                {t("champs.eleve")} *
              </MDTypography>
              <select
                value={form.eleve_id}
                onChange={(e) => setForm((p) => ({ ...p, eleve_id: e.target.value }))}
                style={fieldStyle}
              >
                <option value="">{t("cours.absence.choisirEleve")}</option>
                {eleves.map((e) => (
                  <option key={e.id} value={e.id}>{e.prenom} {e.nom}</option>
                ))}
              </select>
            </MDBox>
          )}

          {/* Date */}
          <MDBox mb={2}>
            <MDTypography variant="caption" fontWeight="bold" sx={{
              display: "block", mb: 0.5, textTransform: "uppercase", fontSize: 11, color: "#9e9e9e",
            }}>
              {t("cours.absence.date")} *
            </MDTypography>
            <input type="date" value={form.date}
              onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
              style={fieldStyle} />
          </MDBox>

          {/* Motif */}
          <MDBox mb={2}>
            <MDTypography variant="caption" fontWeight="bold" sx={{
              display: "block", mb: 0.5, textTransform: "uppercase", fontSize: 11, color: "#9e9e9e",
            }}>
              {t("champs.motif")}
            </MDTypography>
            <input type="text" value={form.motif} placeholder={t("cours.absence.motifPlaceholder")}
              onChange={(e) => setForm((p) => ({ ...p, motif: e.target.value }))}
              style={fieldStyle} />
          </MDBox>

          {/* Commentaire */}
          <MDBox mb={2}>
            <MDTypography variant="caption" fontWeight="bold" sx={{
              display: "block", mb: 0.5, textTransform: "uppercase", fontSize: 11, color: "#9e9e9e",
            }}>
              {t("champs.commentaire")}
            </MDTypography>
            <textarea value={form.commentaire} rows={3}
              placeholder={t("cours.absence.commentairePlaceholder")}
              onChange={(e) => setForm((p) => ({ ...p, commentaire: e.target.value }))}
              style={textareaStyle} />
          </MDBox>

          {/* Rattrapage */}
          <MDBox mb={3}>
            <MDTypography variant="caption" fontWeight="bold" sx={{
              display: "block", mb: 0.5, textTransform: "uppercase", fontSize: 11, color: "#9e9e9e",
            }}>
              {t("cours.absence.rattrapage")}
            </MDTypography>
            <input type="date" value={form.rattrapage_prevu}
              onChange={(e) => setForm((p) => ({ ...p, rattrapage_prevu: e.target.value }))}
              style={fieldStyle} />
          </MDBox>

          <MDBox display="flex" gap={2}>
            <MDButton variant="outlined" color="secondary" onClick={onClose} fullWidth>
              {t("commun.annuler")}
            </MDButton>
            <MDButton variant="gradient" color="error" onClick={handleSubmit}
              disabled={saving} fullWidth>
              {saving ? t("commun.enregistrement") : t("commun.enregistrer")}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

function CoursDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [cours, setCours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEleve, setSelectedEleve] = useState(null);
  const [emailModal, setEmailModal] = useState(false);
  const [absenceModal, setAbsenceModal] = useState(false);
  const [absences, setAbsences] = useState([]);

  useEffect(() => {
    api.get(`/cours/${id}`)
      .then(({ data }) => {
        setCours(data);
        if (data.groupe?.eleves?.length > 0) {
          setSelectedEleve(data.groupe.eleves[0]);
        }
      })
      .finally(() => setLoading(false));
    fetchAbsences();
  }, [id]);

  const fetchAbsences = async () => {
    try {
      const { data } = await api.get(`/absences/cours/${id}`);
      setAbsences(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteAbsence = async (absenceId) => {
    if (!window.confirm(t("cours.absence.confirmerSuppression"))) return;
    try {
      await api.delete(`/absences/${absenceId}`);
      fetchAbsences();
    } catch (e) {
      alert(t("commun.erreurSuppression"));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("cours.confirmerSuppression"))) return;
    try {
      await api.delete(`/cours/${id}`);
      navigate("/cours");
    } catch (error) {
      alert(error.response?.data?.message || t("commun.erreurSuppression"));
    }
  };

  if (loading) return (
    <DashboardLayout><DashboardNavbar />
      <MDBox pt={6} display="flex" justifyContent="center">
        <MDTypography variant="body2" color="text">{t("commun.chargement")}</MDTypography>
      </MDBox>
    </DashboardLayout>
  );

  if (!cours) return (
    <DashboardLayout><DashboardNavbar />
      <MDBox pt={6}>
        <MDTypography variant="body2" color="error">{t("cours.detail.introuvable")}</MDTypography>
      </MDBox>
    </DashboardLayout>
  );

  // Co-enseignants, principal en premier (repli sur l'enseignant unique pour les anciennes données)
  const coEnseignants = cours.coEnseignants?.length
    ? [...cours.coEnseignants].sort((a, b) =>
      Number(b.CoursEnseignant?.est_principal) - Number(a.CoursEnseignant?.est_principal))
    : cours.enseignant ? [{ ...cours.enseignant, CoursEnseignant: { est_principal: true } }] : [];
  const matiere = (MATIERES[cours.matiere] && { ...MATIERES[cours.matiere], label: libelleMatiere(cours.matiere) })
    || (cours.type === "general"
      ? { label: t("valeurs.typeCours.general"), bg: "#e8f5e9", color: "#2e7d32" }
      : { label: cours.matiere, bg: "#f5f5f5", color: "#333" });
  const isGroupe = Boolean(cours.groupe_id);
  const isGeneral = cours.type === "general";
  const theme = TYPE_THEMES[isGeneral ? "general" : isGroupe ? "groupe" : "individuel"];
  const eleves = cours.groupe?.eleves || [];
  const livresGeneral = [...new Set((cours.chapitres || []).map((c) => c.livre?.nom).filter(Boolean))];

  const handleToggleTermine = async (val) => {
    try {
      await api.put(`/cours/${cours.id}`, { chapitre_termine: val });
      setCours((c) => ({ ...c, chapitre_termine: val }));
    } catch (e) {
      alert(e.response?.data?.message || t("commun.erreurMiseAJour"));
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={10}>

            {/* Boutons actions */}
            <MDBox mb={3} display="flex" alignItems="center"
              justifyContent="space-between" flexWrap="wrap" gap={2}>
              <MDButton variant="outlined" color="secondary" size="small"
                onClick={() => navigate("/cours")} sx={{ borderRadius: "10px", px: 2 }}>
                <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
                <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  {t("commun.retourListe")}
                </MDBox>
              </MDButton>
              <MDBox display="flex" gap={1} flexWrap="wrap">
                {!isGeneral && (
                  <MDButton variant="outlined" color="info" size="small"
                    onClick={() => setEmailModal(true)} sx={{ borderRadius: "10px" }}>
                    <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>email</Icon>
                    <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                      {t("commun.envoyerEmail")}
                    </MDBox>
                  </MDButton>
                )}
                <MDButton variant="outlined" color="warning" size="small"
                  onClick={() => navigate(`/cours/${id}/modifier`)} sx={{ borderRadius: "10px" }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>edit</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{t("commun.modifier")}</MDBox>
                </MDButton>
                <MDButton variant="outlined" color="error" size="small"
                  onClick={handleDelete} sx={{ borderRadius: "10px" }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>cancel</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{t("commun.supprimer")}</MDBox>
                </MDButton>
              </MDBox>
            </MDBox>

            {/* ── HEADER COURS ── */}
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", mb: 3, overflow: "hidden" }}>
              <MDBox py={3} px={{ xs: 2, sm: 3 }} sx={{
                background: theme.gradient,
                borderRadius: "0 0 20px 20px",
                boxShadow: `0 4px 20px ${theme.color}40`,
              }}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between"
                  flexWrap="wrap" gap={2}>
                  <MDBox display="flex" alignItems="center" gap={2}>
                    <MDBox sx={{
                      width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 }, borderRadius: "12px",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Icon sx={{ color: "#fff", fontSize: { xs: 22, sm: 28 } }}>
                        {theme.icon}
                      </Icon>
                    </MDBox>
                    <MDBox>
                      <MDTypography variant="h5" color="white" fontWeight="bold"
                        sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}>
                        {matiere.label}
                      </MDTypography>
                      <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <MDBox sx={{
                          display: "inline-flex", alignItems: "center", gap: 0.5,
                          padding: "3px 12px", borderRadius: "20px",
                          background: "rgba(255,255,255,0.85)", color: theme.color,
                          fontSize: 11, fontWeight: 700, letterSpacing: "0.02em",
                        }}>
                          <Icon sx={{ fontSize: "14px !important" }}>{theme.icon}</Icon>
                          {isGroupe && cours.groupe?.nom ? `${t(theme.label)} · ${cours.groupe.nom}` : t(theme.label)}
                        </MDBox>
                        <MDTypography variant="caption" color="white" sx={{
                          opacity: 0.85, display: { xs: "none", sm: "block" },
                        }}>
                          {formatPlanning(cours) || t("cours.detail.horaireNonDefini")}
                          {cours.lieu && ` · 📍 ${cours.lieu}`}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                  <MDBox sx={{
                    ...pulseKeyframes,
                    display: "inline-flex", alignItems: "center", gap: 0.75,
                    padding: "6px 16px", borderRadius: "999px",
                    background: cours.chapitre_termine ? "#fff" : "rgba(255,255,255,0.15)",
                    color: cours.chapitre_termine ? theme.color : "#fff",
                    border: "1px solid rgba(255,255,255,0.5)",
                    backdropFilter: "blur(4px)",
                    fontWeight: 600, fontSize: 13,
                  }}>
                    {cours.chapitre_termine ? (
                      <CheckCircleIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <HourglassEmptyIcon sx={{
                        fontSize: 18, animation: "coursPulse 1.6s ease-in-out infinite",
                      }} />
                    )}
                    {cours.chapitre_termine ? t("commun.termine") : t("commun.enCours")}
                  </MDBox>
                </MDBox>
              </MDBox>

              <MDBox pt={3} pb={3} px={{ xs: 2, sm: 3 }}>
                <Grid container spacing={2}>

                  {/* Cours général / conférence */}
                  {isGeneral && (
                    <Grid item xs={12} md={6}>
                      <MDBox p={3} sx={cardStyle}>
                        <MDBox sx={{
                          display: "inline-flex", alignItems: "center", gap: 0.5,
                          padding: "4px 10px", borderRadius: "999px",
                          background: "#e8f5e9", color: "#2e7d32",
                          fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                          letterSpacing: "0.06em", mb: 2.5,
                        }}>
                          <Icon sx={{ fontSize: "15px !important" }}>campaign</Icon>
                          {t("valeurs.typeCours.general")}
                        </MDBox>
                        {[
                          [t("cours.detail.intitule"), `${matiere.label}${livresGeneral.length ? ` — ${livresGeneral.join(", ")}` : ""}`],
                          [t("champs.enseignants"), coEnseignants.map((e) => `${e.prenom} ${e.nom}`).join(", ") || "—"],
                          [t("champs.participants"), cours.nb_participants != null ? `${cours.nb_participants}` : "—"],
                          [t("cours.form.planification"), formatPlanning(cours) || t("cours.detail.horaireNonDefini")],
                        ].map(([label, val]) => (
                          <MDBox key={label} sx={{
                            display: "grid", gridTemplateColumns: { xs: "1fr", sm: "130px 1fr" },
                            alignItems: "baseline", columnGap: 2, rowGap: 0.25, mb: 1.75,
                          }}>
                            <MDTypography component="span" sx={{
                              color: "#9e9e9e", fontSize: 12, fontWeight: 500,
                              textTransform: "uppercase", letterSpacing: "0.06em",
                            }}>
                              {label}
                            </MDTypography>
                            <MDTypography component="span" sx={{
                              color: "#1a1a1a", fontSize: 15, fontWeight: 500,
                            }}>
                              {val}
                            </MDTypography>
                          </MDBox>
                        ))}
                        <Divider sx={{ my: 2, opacity: 1, background: "#eeeeee" }} />
                        <MDTypography component="div" sx={{
                          color: "#9e9e9e", fontSize: 12, fontWeight: 500,
                          textTransform: "uppercase", letterSpacing: "0.06em", mb: 1,
                        }}>
                          {t("champs.commentaire")}
                        </MDTypography>
                        <MDBox sx={{
                          background: "#f8f8f8", borderLeft: "3px solid #4caf50",
                          padding: "12px", borderRadius: "8px",
                        }}>
                          {cours.commentaire_general ? (
                            <MDTypography sx={{ color: "#1a1a1a", fontSize: 14, whiteSpace: "pre-line" }}>
                              {cours.commentaire_general}
                            </MDTypography>
                          ) : (
                            <MDTypography sx={{ color: "#9e9e9e", fontSize: 14, fontStyle: "italic" }}>
                              {t("cours.detail.aucunCommentaire")}
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </Grid>
                  )}

                  {/* Élève individuel */}
                  {!isGroupe && !isGeneral && (
                    <Grid item xs={12} md={6}>
                      <MDBox p={3} sx={cardStyle}>
                        <MDTypography component="div" sx={{ ...labelStyle, mb: 2.5 }}>
                          {t("champs.eleve")}
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={2}>
                          <AvatarCircle prenom={cours.eleve?.prenom} nom={cours.eleve?.nom}
                            photo={cours.eleve?.photo} color="#1565c0" />
                          <MDBox>
                            <MDTypography sx={{ color: "#1a1a1a", fontSize: 16, fontWeight: 600 }}>
                              {cours.eleve?.prenom} {cours.eleve?.nom}
                            </MDTypography>
                            {cours.eleve?.email && (
                              <MDTypography variant="caption" color="text"
                                sx={{ display: { xs: "none", sm: "block" } }}>
                                ✉️ <bdi dir="ltr">{cours.eleve.email}</bdi>
                              </MDTypography>
                            )}
                            {cours.eleve?.telephone && (
                              <MDTypography variant="caption" color="text" display="block">
                                📞 <bdi dir="ltr">{cours.eleve.telephone}</bdi>
                              </MDTypography>
                            )}
                          </MDBox>
                        </MDBox>
                        <MDBox mt={2.5}>
                          <MDButton variant="outlined" color="info" size="small"
                            onClick={() => navigate(`/eleves/${cours.eleve_id}`)}
                            sx={{ borderRadius: "8px", fontSize: 11 }}>
                            {t("cours.detail.voirFicheEleve")}
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </Grid>
                  )}

                  {/* Groupe */}
                  {isGroupe && (
                    <Grid item xs={12} md={6}>
                      <MDBox p={3} sx={cardStyle}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <MDBox>
                            <MDTypography component="div" sx={labelStyle}>
                              {t("champs.groupe")}
                            </MDTypography>
                            <MDTypography sx={{ color: "#1a1a1a", fontSize: 15, fontWeight: 500 }}>
                              {cours.groupe?.nom}
                              <MDBox component="span" sx={{ color: "#9e9e9e", fontSize: 13, ml: 1 }}>
                                {t("cours.nbEleves", { count: eleves.length })}
                              </MDBox>
                            </MDTypography>
                          </MDBox>
                          <MDButton variant="outlined" color="info" size="small"
                            onClick={() => navigate(`/groupes/${cours.groupe_id}`)}
                            sx={{ borderRadius: "8px", fontSize: 11 }}>
                            {t("cours.detail.voir")}
                          </MDButton>
                        </MDBox>
                        <MDBox sx={{ maxHeight: 200, overflowY: "auto" }}>
                          {eleves.map((eleve) => {
                            const actif = selectedEleve?.id === eleve.id;
                            return (
                              <MDBox key={eleve.id}
                                display="flex" alignItems="center" gap={1.5}
                                py={1} px={1.5} mb={0.5}
                                sx={{
                                  borderRadius: "10px", cursor: "pointer",
                                  background: actif ? `${theme.color}0f` : "#fff",
                                  border: "1px solid",
                                  borderColor: actif ? theme.color : "#eeeeee",
                                  transition: "all 0.2s",
                                  "&:hover": { borderColor: actif ? theme.color : "#bdbdbd" },
                                }}
                                onClick={() => setSelectedEleve(eleve)}>
                                <AvatarCircle prenom={eleve.prenom} nom={eleve.nom}
                                  photo={eleve.photo} color="#2e7d32" />
                                <MDBox flex={1}>
                                  <MDTypography sx={{
                                    color: "#1a1a1a", fontSize: 14, fontWeight: actif ? 600 : 500,
                                  }}>
                                    {eleve.prenom} {eleve.nom}
                                  </MDTypography>
                                </MDBox>
                                <MDBox onClick={(e) => { e.stopPropagation(); navigate(`/eleves/${eleve.id}`); }}
                                  sx={{
                                    width: 28, height: 28, borderRadius: "8px",
                                    background: "#f5f5f5", color: "#616161",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer",
                                    "&:hover": { background: "#e3f2fd", color: "#1A73E8" },
                                  }}>
                                  <Icon sx={{ fontSize: 14 }}>visibility</Icon>
                                </MDBox>
                              </MDBox>
                            );
                          })}
                        </MDBox>
                      </MDBox>
                    </Grid>
                  )}

                  {/* Enseignants (co-enseignants, principal en premier) */}
                  <Grid item xs={12} md={6}>
                    <MDBox p={3} sx={cardStyle}>
                      <MDTypography component="div" variant="subtitle2" sx={{ ...labelStyle, mb: 2 }}>
                        {t("champs.enseignants")}
                      </MDTypography>
                      {coEnseignants.length === 0 ? (
                        <MDTypography variant="caption" color="text">{t("cours.detail.aucunEnseignant")}</MDTypography>
                      ) : coEnseignants.map((e) => (
                        <Chip
                          key={e.id}
                          label={`${e.prenom} ${e.nom}${e.CoursEnseignant?.est_principal ? ` (${t("cours.detail.principal")})` : ""}`}
                          size="small"
                          onClick={() => navigate(`/enseignants/${e.id}`)}
                          color={e.CoursEnseignant?.est_principal ? "secondary" : "default"}
                          variant={e.CoursEnseignant?.est_principal ? "filled" : "outlined"}
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </MDBox>
                  </Grid>

                  {cours.examen && (
                    <Grid item xs={12}>
                      <MDBox p={3} sx={cardStyle}>
                        <MDTypography component="div" sx={{ ...labelStyle, mb: 1 }}>
                          {t("cours.detail.notesExamen")}
                        </MDTypography>
                        <MDBox sx={{
                          background: "#f8f8f8", borderLeft: "3px solid #ffb300",
                          padding: "12px", borderRadius: "8px",
                        }}>
                          <MDTypography sx={{ color: "#1a1a1a", fontSize: 14, whiteSpace: "pre-line" }}>
                            {cours.examen}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>

            {/* ── STATUT COURS GÉNÉRAL (standalone) ── */}
            {isGeneral && (
              <MDBox mb={3} sx={{
                display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
                padding: { xs: "16px", sm: "18px 24px" }, borderRadius: "12px",
                background: cours.chapitre_termine ? "#f1f8e9" : "#fff",
                border: `1.5px solid ${cours.chapitre_termine ? "#4caf50" : "#e0e0e0"}`,
                transition: "all 0.2s ease",
              }}>
                {cours.chapitre_termine ? (
                  <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 28 }} />
                ) : (
                  <HourglassEmptyIcon sx={{ color: "#9e9e9e", fontSize: 28 }} />
                )}
                <MDBox flex={1} minWidth={160}>
                  <MDTypography sx={{
                    fontSize: 16, fontWeight: 600, lineHeight: 1.3,
                    color: cours.chapitre_termine ? "#2e7d32" : "#616161",
                  }}>
                    {cours.chapitre_termine ? t("cours.detail.coursTermine") : t("cours.detail.coursEnCours")}
                  </MDTypography>
                  <MDTypography sx={{
                    fontSize: 13, color: cours.chapitre_termine ? "#66bb6a" : "#9e9e9e",
                  }}>
                    {cours.chapitre_termine ? t("commun.cliquerPourRouvrir") : t("commun.marquerTermine")}
                  </MDTypography>
                </MDBox>
                {cours.chapitre_termine ? (
                  <MDButton variant="outlined" size="small"
                    onClick={() => handleToggleTermine(false)}
                    sx={{
                      borderRadius: "8px", padding: "8px 20px",
                      borderColor: "#e57373", color: "#e57373",
                      "&:hover": { borderColor: "#e57373", background: "#ffebee" },
                    }}>
                    {t("commun.rouvrir")}
                  </MDButton>
                ) : (
                  <MDButton variant="outlined" size="small"
                    onClick={() => handleToggleTermine(true)}
                    sx={{
                      borderRadius: "8px", padding: "8px 20px",
                      borderColor: "#4caf50", color: "#2e7d32",
                      "&:hover": { borderColor: "#4caf50", background: "#f1f8e9" },
                    }}>
                    {t("suivi.terminerCours")}
                  </MDButton>
                )}
              </MDBox>
            )}

            {/* ── SECTION ABSENCES ── */}
            {!isGeneral && (
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", mb: 3 }}>
              <MDBox mx={2} mt={-3} py={2.5} px={{ xs: 2, sm: 3 }} sx={{
                background: "linear-gradient(135deg, #c62828, #e53935)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(198,40,40,0.4)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <MDBox display="flex" alignItems="center" gap={1.5}>
                  <Icon sx={{ color: "#fff", fontSize: 22 }}>event_busy</Icon>
                  <MDBox>
                    <MDTypography variant="h6" color="white" fontWeight="bold">
                      {t("cours.detail.absences")}
                    </MDTypography>
                    <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                      {t("cours.detail.absencesCompteur", { count: absences.length })}
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDButton variant="contained" size="small"
                  onClick={() => setAbsenceModal(true)}
                  sx={{
                    background: "#fff", color: "#c62828", fontWeight: 700,
                    borderRadius: "10px", "&:hover": { background: "#ffebee" },
                  }}>
                  <Icon sx={{ mr: 0.5, fontSize: 16 }}>add</Icon>
                  {t("cours.detail.signaler")}
                </MDButton>
              </MDBox>

              <MDBox p={3}>
                {absences.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
                    <Icon sx={{ fontSize: 48, color: "#e0e0e0" }}>event_available</Icon>
                    <MDTypography variant="body2" color="text">
                      {t("eleves.aucuneAbsence")}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox>
                    {absences.map((absence, index) => (
                      <MDBox key={absence.id}
                        display="flex" alignItems="flex-start" gap={2}
                        py={1.5} px={2} mb={1}
                        sx={{
                          background: index % 2 === 0 ? "#fff5f5" : "#fff",
                          borderRadius: "12px", border: "1px solid #ffcdd2",
                          transition: "all 0.2s",
                          "&:hover": { background: "#ffebee" },
                        }}>
                        {/* Avatar élève */}
                        <MDBox sx={{
                          width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                          background: "#c62828", color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 700, fontSize: 13, overflow: "hidden",
                        }}>
                          {absence.eleve?.photo ? (
                            <img src={absence.eleve.photo} alt=""
                              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            `${absence.eleve?.prenom?.[0] || ""}${absence.eleve?.nom?.[0] || ""}`
                          )}
                        </MDBox>

                        {/* Infos */}
                        <MDBox flex={1}>
                          <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#344767" }}>
                              {absence.eleve?.prenom} {absence.eleve?.nom}
                            </MDTypography>
                            <MDBox sx={{
                              padding: "1px 8px", borderRadius: "8px",
                              background: "#ffebee", color: "#c62828",
                              fontSize: 11, fontWeight: 600,
                            }}>
                              📅 {formatDate(absence.date)}
                            </MDBox>
                            {absence.rattrapage_prevu && (
                              <MDBox sx={{
                                padding: "1px 8px", borderRadius: "8px",
                                background: "#e8f5e9", color: "#2e7d32",
                                fontSize: 11, fontWeight: 600,
                              }}>
                                🔄 {t("eleves.rattrapageLe", { date: formatDate(absence.rattrapage_prevu) })}
                              </MDBox>
                            )}
                          </MDBox>
                          {absence.motif && (
                            <MDTypography variant="caption" color="text" display="block" mt={0.3}>
                              📝 {absence.motif}
                            </MDTypography>
                          )}
                          {absence.commentaire && (
                            <MDTypography variant="caption" color="text" display="block" mt={0.3}
                              sx={{ fontStyle: "italic" }}>
                              {absence.commentaire}
                            </MDTypography>
                          )}
                        </MDBox>

                        {/* Supprimer */}
                        <MDBox onClick={() => handleDeleteAbsence(absence.id)} sx={{
                          width: 28, height: 28, borderRadius: "8px", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer", color: "#e0e0e0",
                          "&:hover": { background: "#ffebee", color: "#c62828" },
                        }}>
                          <Icon sx={{ fontSize: 16 }}>delete</Icon>
                        </MDBox>
                      </MDBox>
                    ))}
                  </MDBox>
                )}
              </MDBox>
            </Card>
            )}

            {/* Suivi des chapitres (pas pour les cours généraux) */}
            {!isGeneral && (cours.eleve_id || selectedEleve) && (
              <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
                <MDBox mx={2} mt={-3} py={2.5} px={{ xs: 2, sm: 3 }} sx={{
                  background: "linear-gradient(135deg, #1976d2, #1565c0)",
                  borderRadius: "12px", boxShadow: "0 4px 20px rgba(21,101,192,0.4)",
                }}>
                  <MDBox display="flex" alignItems="center" gap={1.5}>
                    <Icon sx={{ color: "#fff", fontSize: 22 }}>timeline</Icon>
                    <MDBox>
                      <MDTypography variant="h6" color="white" fontWeight="bold">
                        {t("eleves.sections.suiviChapitres")}
                      </MDTypography>
                      <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                        {isGroupe
                          ? `${selectedEleve?.prenom} ${selectedEleve?.nom}`
                          : `${cours.eleve?.prenom} ${cours.eleve?.nom}`}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
                <MDBox pt={3} pb={3} px={{ xs: 2, sm: 3 }}>
                  {isGroupe && eleves.length > 0 && (
                    <MDBox display="flex" gap={1} flexWrap="wrap" mb={3}>
                      {eleves.map((eleve) => (
                        <MDBox key={eleve.id} onClick={() => setSelectedEleve(eleve)} sx={{
                          padding: "6px 16px", borderRadius: "20px", cursor: "pointer",
                          border: "1px solid",
                          borderColor: selectedEleve?.id === eleve.id ? "#1565c0" : "#d2d6da",
                          background: selectedEleve?.id === eleve.id ? "#1565c0" : "#fff",
                          color: selectedEleve?.id === eleve.id ? "#fff" : "#344767",
                          fontSize: 13, fontWeight: 500, transition: "all 0.2s",
                        }}>
                          {eleve.prenom} {eleve.nom}
                        </MDBox>
                      ))}
                    </MDBox>
                  )}
                  <SuiviTimeline
                    eleveId={isGroupe ? selectedEleve?.id : cours.eleve_id}
                    coursId={cours.id}
                    enseignantDefaut={cours.enseignant}
                    onTermineChange={(val) => setCours((c) => ({ ...c, chapitre_termine: val }))}
                  />
                </MDBox>
              </Card>
            )}

          </Grid>
        </Grid>
      </MDBox>

      <AbsenceModal
        open={absenceModal}
        onClose={() => setAbsenceModal(false)}
        cours={cours}
        onSaved={fetchAbsences}
      />

      <EmailModal
        open={emailModal}
        onClose={() => setEmailModal(false)}
        eleveId={!isGroupe ? cours.eleve_id : null}
        groupeId={isGroupe ? cours.groupe_id : null}
        nomDestinataire={isGroupe
          ? t("cours.detail.groupeNom", { nom: cours.groupe?.nom })
          : `${cours.eleve?.prenom} ${cours.eleve?.nom}`}
        elevesGroupe={isGroupe ? eleves : null}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default CoursDetail;